import * as CounterIndicationsActions from './counter-indications.actions';

import {createReducer, on} from '@ngrx/store';
import {createEntityAdapter, EntityAdapter, EntityState} from '@ngrx/entity';
import {DahPaginationModel} from '@common/models/commonHTTP.models';
import {DahLoadingType} from '@common/components-module/core-components.models';
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {AcCounters, AcCountersSearchParams, AcCounterType, TotalConsumed} from '@accounting/_common/models/counter-indications.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';
import {ResourceType} from '@accounting/_common/models/_accounting.models';

export const countersIndicationsFeatureKey = 'counterIndications';

export const adapter: EntityAdapter<AcCounters> = createEntityAdapter<AcCounters>();

export interface CountersIndicationsState {
    APARTMENT: CounterTypeState,
    ORGANIZATION: CounterTypeState
}

export interface CounterTypeState {
    period: string,
    filter: AcCountersSearchParams,
    indications: Record<string, IndicationsState>
}

export const counterTypeStateInitialState: CounterTypeState = {
    period: '',
    filter: {},
    indications: {}
};

export interface IndicationsState extends EntityState<AcCounters> {
    totalConsumed: TotalConsumed,
    pagination: DahPaginationModel,
    listLoading: DahLoadingType,
    itemsLoading: string[],
}

export const resourceTypeInitialState: IndicationsState = adapter.getInitialState({
    totalConsumed: {},
    pagination: {},
    listLoading: 'listLoading',
    itemsLoading: [],
});

export const initialState: CountersIndicationsState = {
    APARTMENT: counterTypeStateInitialState,
    ORGANIZATION: counterTypeStateInitialState
};

export const countersIndicationsReducer = createReducer(
    initialState,

    on(CounterIndicationsActions.LoadList, (state, action) => {
        if (!state[action.countersType].indications[action.payload.resourceType]) {
            state = initResourceTypeState(state, action.countersType, action.payload.resourceType);
        }

        return {
            ...state,
            [action.countersType]: {
                ...state[action.countersType],
                indications: {
                    ...state[action.countersType].indications,
                    [action.payload.resourceType]: {
                        ...state[action.countersType].indications[action.payload.resourceType],
                        listLoading: action.payload.loading
                    }
                }
            }
        };
    }),

    on(CounterIndicationsActions.LoadListSuccess, (state, action) => {
        if (!state[action.countersType].indications[action.payload.resourceType]) {
            state = initResourceTypeState(state, action.countersType, action.payload.resourceType);
        }

        return {
            ...state,
            [action.countersType]: {
                ...state[action.countersType],
                indications: {
                    ...state[action.countersType].indications,
                    [action.payload.resourceType]: action.payload.clearState
                        ? adapter.setAll(action.payload.items, {
                            ...state[action.countersType].indications[action.payload.resourceType],
                            listLoading: undefined,
                            pagination: action.payload.pagination
                        })
                        : adapter.setMany(action.payload.items, {
                            ...state[action.countersType].indications[action.payload.resourceType],
                            listLoading: undefined,
                            pagination: action.payload.pagination
                        })

                }
            }
        };
    }),

    on(CounterIndicationsActions.LoadListFailure, (state, action) => {
        if (!state[action.countersType].indications[action.payload.resourceType]) {
            state = initResourceTypeState(state, action.countersType, action.payload.resourceType);
        }

        return {
            ...state,
            [action.countersType]: {
                ...state[action.countersType],
                indications: {
                    ...state[action.countersType].indications,
                    [action.payload.resourceType]: {
                        ...state[action.countersType].indications[action.payload.resourceType],
                        listLoading: undefined
                    }
                }
            }
        };
    }),

    on(CounterIndicationsActions.LoadTotalConsumedSuccess, (state, action) => {
        if (!state[action.countersType].indications[action.payload.resourceType]) {
            state = initResourceTypeState(state, action.countersType, action.payload.resourceType);
        }

        return {
            ...state,
            [action.countersType]: {
                ...state[action.countersType],
                indications: {
                    ...state[action.countersType].indications,
                    [action.payload.resourceType]: {
                        ...state[action.countersType].indications[action.payload.resourceType],
                        totalConsumed: action.payload.totalConsumed
                    }
                }
            }
        };
    }),

    on(CounterIndicationsActions.SetPagination, (state, action) => {
        if (!state[action.countersType].indications[action.payload.resourceType]) {
            state = initResourceTypeState(state, action.countersType, action.payload.resourceType);
        }

        return {
            ...state,
            [action.countersType]: {
                ...state[action.countersType],
                indications: {
                    ...state[action.countersType].indications,
                    [action.payload.resourceType]: {
                        ...state[action.countersType].indications[action.payload.resourceType],
                        pagination: {
                            ...state[action.countersType].indications[action.payload.resourceType].pagination,
                            number: action.payload.next ? StoredComponentHelperService.setNextPage(state[action.countersType].indications[action.payload.resourceType].pagination, state[action.countersType].indications[action.payload.resourceType].ids?.length) : 0
                        }
                    }
                }
            }
        };
    }),

    on(CounterIndicationsActions.SetLoading, (state, action) => {
        if (!state[action.countersType].indications[action.payload.resourceType]) {
            state = initResourceTypeState(state, action.countersType, action.payload.resourceType);
        }

        return {
            ...state,
            [action.countersType]: {
                ...state[action.countersType],
                indications: {
                    ...state[action.countersType].indications,
                    [action.payload.resourceType]: {
                        ...state[action.countersType].indications[action.payload.resourceType],
                        listLoading: action.payload.loading
                    }
                }
            }
        };
    }),

    on(CounterIndicationsActions.SetItemsLoading, (state, action) => {
        if (!state[action.countersType].indications[action.payload.resourceType]) {
            state = initResourceTypeState(state, action.countersType, action.payload.resourceType);
        }

        return {
            ...state,
            [action.countersType]: {
                ...state[action.countersType],
                indications: {
                    ...state[action.countersType].indications,
                    [action.payload.resourceType]: {
                        ...state[action.countersType].indications[action.payload.resourceType],
                        itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.countersType].indications[action.payload.resourceType].itemsLoading, action.payload.id, action.payload.isStart)
                    }
                }
            }
        };
    }),

    on(CounterIndicationsActions.UpdateListItem, (state, action) => {
        if (!state[action.countersType].indications[action.payload.resourceType]) {
            state = initResourceTypeState(state, action.countersType, action.payload.resourceType);
        }

        return {
            ...state,
            [action.countersType]: {
                ...state[action.countersType],
                indications: {
                    ...state[action.countersType].indications,
                    [action.payload.resourceType]: adapter.updateOne(({id: action.payload.item.id!, changes: action.payload.item}), {
                        ...state[action.countersType].indications[action.payload.resourceType],
                        itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.countersType].indications[action.payload.resourceType].itemsLoading, action.payload.item.id!, false)
                    })
                }
            }
        };
    }),

    on(CounterIndicationsActions.SetPeriod, (state, action) => {
        return {
            ...state,
            [action.countersType]: {
                ...state[action.countersType],
                period: action.payload.period
            }
        };
    }),

    on(CounterIndicationsActions.SetFilter, (state, action) => {
        return {
            ...state,
            [action.countersType]: {
                ...state[action.countersType],
                filter: action.payload.filter
            }
        };
    }),

    on(CounterIndicationsActions.SaveFirstPage, (state, action) => {
        return {
            ...state,
            [action.countersType]: {
                ...state[action.countersType],
                indications: {
                    ...state[action.countersType].indications,
                    [action.payload.resourceType]: adapter.setAll(action.payload.items, {
                        ...state[action.countersType].indications[action.payload.resourceType],
                        listLoading: undefined,
                        pagination: {...state[action.countersType].indications[action.payload.resourceType].pagination, number: 0}
                    })
                }
            }
        };
    }),

    on(CounterIndicationsActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const selectAllEntities = (countersType: AcCounterType, resourceType: ResourceType) => adapter.getSelectors((state: CountersIndicationsState) => {
    if (!state[countersType].indications[resourceType]) {
        state = initResourceTypeState(state, countersType, resourceType);
    }

    return state[countersType].indications[resourceType];
}).selectAll;

export const initResourceTypeState = (state: CountersIndicationsState, countersType: AcCounterType, resourceType: ResourceType): CountersIndicationsState => {
    return {
        ...state,
        [countersType]: {
            ...state[countersType],
            indications: {
                ...state[countersType].indications,
                [resourceType]: resourceTypeInitialState
            }
        }
    };
};


